import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, type AccountSecurity, type ProviderAvailability } from './api';
import type { AuthUser, Game, PlayerProfile } from './types';

export type AuthStatus = 'loading' | 'guest' | 'authed';

interface Toast {
  id: number;
  message: string;
  tone: 'info' | 'success' | 'error';
}

interface AppState {
  authStatus: AuthStatus;
  user: AuthUser | null;
  profile: PlayerProfile | null;
  account: AccountSecurity | null;
  providers: ProviderAvailability;
  games: Game[];
  refreshAuth: () => Promise<void>;
  setProfile: (profile: PlayerProfile) => void;
  logout: () => Promise<void>;
  toast: (message: string, tone?: Toast['tone']) => void;
  toasts: Toast[];
}

const AppContext = createContext<AppState | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [authStatus, setAuthStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [profile, setProfile] = useState<PlayerProfile | null>(null);
  const [account, setAccount] = useState<AccountSecurity | null>(null);
  const [providers, setProviders] = useState<ProviderAvailability>({ discord: false, google: false, emailSending: false });
  const [games, setGames] = useState<Game[]>([]);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastId = useRef(0);

  const toast = useCallback((message: string, tone: Toast['tone'] = 'info') => {
    const id = ++toastId.current;
    setToasts((current) => [...current, { id, message, tone }]);
    window.setTimeout(() => setToasts((current) => current.filter((item) => item.id !== id)), 4200);
  }, []);

  const refreshAuth = useCallback(async () => {
    try {
      const state = await api.authState();
      setUser(state.user);
      setProfile(state.profile);
      setAccount(state.account);
      setProviders(state.providers);
      setAuthStatus(state.authenticated ? 'authed' : 'guest');
    } catch {
      setAuthStatus('guest');
      setUser(null);
      setProfile(null);
      setAccount(null);
    }
  }, []);

  const logout = useCallback(async () => {
    await api.logout();
    setUser(null);
    setProfile(null);
    setAccount(null);
    setAuthStatus('guest');
  }, []);

  useEffect(() => {
    api.authState()
      .then((state) => {
        setUser(state.user);
        setProfile(state.profile);
        setAccount(state.account);
        setProviders(state.providers);
        setAuthStatus(state.authenticated ? 'authed' : 'guest');
      })
      .catch(() => {
        setAuthStatus('guest');
        setUser(null);
        setProfile(null);
        setAccount(null);
      });
    api.games().then((data) => setGames(data.games)).catch(() => setGames([]));
  }, []);

  const value = useMemo(() => ({ authStatus, user, profile, account, providers, games, refreshAuth, setProfile, logout, toast, toasts }),
    [authStatus, user, profile, account, providers, games, refreshAuth, logout, toast, toasts]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used inside AppProvider');
  return context;
}
